<template>
  <div
    class="ai-cli-msg"
    :class="`ai-cli-msg--${message.role}`"
  >
    <div
      v-if="message.role === 'user'"
      class="ai-cli-msg__user"
    >
      <span class="ai-cli-msg__prompt">&gt;</span>
      <span class="ai-cli-msg__user-text">{{ message.text }}</span>
      <span class="ai-cli-msg__time">{{ formatTime(message.at) }}</span>
    </div>

    <div
      v-else
      class="ai-cli-msg__assistant"
    >
      <div
        v-if="message.plan"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--plan">[plan]</span>
        <span class="ai-cli-msg__block-text">{{ message.plan }}</span>
      </div>

      <!--
        Chronological view: the progress notes, the tool calls and the answer in
        the order they happened. A note is a text block the model wrote before
        calling a tool, so it is dimmed; the trailing block is the answer and
        gets the full treatment.
      -->
      <template v-if="timeline">
        <div
          v-for="entry in timeline"
          :key="entry.key"
          class="ai-cli-msg__block"
          :class="{ 'ai-cli-msg__tool': entry.kind === 'tool' }"
        >
          <template v-if="entry.kind === 'note'">
            <span class="ai-cli-msg__tag ai-cli-msg__tag--note">·</span>
            <span class="ai-cli-msg__block-text ai-cli-msg__note">{{ entry.text.trim() }}</span>
          </template>

          <template v-else-if="entry.kind === 'answer'">
            <RenderedAnswer
              class="ai-cli-msg__answer"
              :text="entry.text"
            />
          </template>

          <template v-else>
            <button
              type="button"
              class="ai-cli-msg__tool-head"
              :aria-expanded="isToolOpen(entry.id) ? 'true' : 'false'"
              @click="toggleTool(entry.id)"
            >
              <span
                class="ai-cli-msg__tag"
                :class="entry.status === 'running' ? 'ai-cli-msg__tag--running' : 'ai-cli-msg__tag--ok'"
              >
                {{ entry.status === 'running' ? '[run ]' : '[ok  ]' }}
              </span>
              <code class="ai-cli-msg__tool-name">{{ entry.name }}</code>
              <span
                v-if="entry.status === 'running'"
                class="ai-cli-msg__spinner"
              />
              <span class="ai-cli-msg__tool-meta">{{ isToolOpen(entry.id) ? 'hide' : 'details' }}</span>
              <span
                class="ai-cli-msg__chev"
                :class="{ 'ai-cli-msg__chev--open': isToolOpen(entry.id) }"
              >›</span>
            </button>
            <template v-if="isToolOpen(entry.id)">
              <pre class="ai-cli-msg__json ai-cli-msg__json--args">{{ formatJson(entry.args) }}</pre>
              <pre
                v-if="entry.result !== undefined && entry.result !== null"
                class="ai-cli-msg__json ai-cli-msg__json--result"
              >{{ formatJson(entry.result) }}</pre>
            </template>
          </template>
        </div>
      </template>

      <!-- Fallback for sessions saved before the timeline existed. -->
      <template v-else>
        <div
          v-for="tool in message.tools"
          :key="tool.id"
          class="ai-cli-msg__block ai-cli-msg__tool"
        >
          <button
            type="button"
            class="ai-cli-msg__tool-head"
            :aria-expanded="isToolOpen(tool.id) ? 'true' : 'false'"
            @click="toggleTool(tool.id)"
          >
            <span
              class="ai-cli-msg__tag"
              :class="tool.status === 'running' ? 'ai-cli-msg__tag--running' : 'ai-cli-msg__tag--ok'"
            >
              {{ tool.status === 'running' ? '[run ]' : '[ok  ]' }}
            </span>
            <code class="ai-cli-msg__tool-name">{{ tool.name }}</code>
            <span
              v-if="tool.status === 'running'"
              class="ai-cli-msg__spinner"
            />
            <span class="ai-cli-msg__tool-meta">{{ isToolOpen(tool.id) ? 'hide' : 'details' }}</span>
            <span
              class="ai-cli-msg__chev"
              :class="{ 'ai-cli-msg__chev--open': isToolOpen(tool.id) }"
            >›</span>
          </button>
          <template v-if="isToolOpen(tool.id)">
            <pre class="ai-cli-msg__json ai-cli-msg__json--args">{{ formatJson(tool.args) }}</pre>
            <pre
              v-if="tool.result !== undefined"
              class="ai-cli-msg__json ai-cli-msg__json--result"
            >{{ formatJson(tool.result) }}</pre>
          </template>
        </div>

        <div
          v-if="message.answer"
          class="ai-cli-msg__block"
        >
          <RenderedAnswer
            class="ai-cli-msg__answer"
            :text="message.answer"
          />
        </div>
      </template>

      <div
        v-if="chartData"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--link">[chart]</span>
        <div class="ai-cli-msg__chart-wrap">
          <button
            type="button"
            class="ai-cli-msg__chart-toggle"
            @click="showChart = !showChart"
          >
            {{ showChart ? 'hide chart' : 'view as chart' }}
          </button>
          <AiAgentChart
            v-if="showChart"
            :data="chartData"
          />
        </div>
      </div>

      <div
        v-if="message.status === 'streaming' && !message.answer && !message.tools.length && !message.plan"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--plan">[...]</span>
        <span class="ai-cli-msg__block-text ai-cli-msg__pulse">waiting</span>
      </div>

      <div
        v-if="message.status === 'error'"
        class="ai-cli-msg__block ai-cli-msg__error"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--err">[err ]</span>
        <AiAgentErrorCard
          :error="message.error || 'unknown error'"
          :seed="message.id"
          :at="message.lastAt || message.at || 0"
          :diagnostics="diagnostics"
        />
      </div>

      <div
        v-if="message.status === 'done' || message.status === 'error'"
        class="ai-cli-msg__block ai-cli-msg__done"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--done">[done]</span>
        <span class="ai-cli-msg__block-text">
          {{ duration }} · {{ message.tools.length }} tool {{ message.tools.length === 1 ? 'call' : 'calls' }}
        </span>
      </div>

      <div
        v-if="message.citations.length"
        class="ai-cli-msg__block"
      >
        <span class="ai-cli-msg__tag ai-cli-msg__tag--link">[link]</span>
        <CitationLinks
          :items="message.citations"
          container-class="ai-cli-msg__links"
          link-class="ai-cli-msg__link"
        />
      </div>
    </div>
  </div>
</template>

<script>
import RenderedAnswer from './RenderedAnswer.vue'
import CitationLinks from './CitationLinks.vue'
import AiAgentChart from './AiAgentChart.vue'
import AiAgentErrorCard from './AiAgentErrorCard.vue'
import { isValidMessage } from './message-shape'
import { extractChartData } from './chartable'

export default {
  name: 'AiAgentCliMessage',

  components: { RenderedAnswer, CitationLinks, AiAgentChart, AiAgentErrorCard },

  props: {
    message: {
      type: Object,
      required: true,
      validator: isValidMessage
    }
  },

  data() {
    return {
      showChart: false,
      // Tool args/results are collapsed by default to keep the transcript
      // scannable; an admin expands a specific call on demand. Keyed by
      // tool id so each call remembers its own open/closed state.
      expandedTools: {}
    }
  },

  computed: {
    // Text blocks and tool calls in the order they happened. The last text block
    // is the answer; earlier ones are progress notes the model wrote on its way
    // to a tool call. Returns null when the message predates the timeline, and
    // the template falls back to the flat tools-then-answer layout.
    timeline() {
      const entries = this.message.timeline
      if (!Array.isArray(entries) || !entries.length) {
        return null
      }

      const texts = entries.filter(e => e.kind === 'text' && e.text.trim())
      const answerId = texts.length ? texts[texts.length - 1].id : null

      return entries
        .filter(e => e.kind !== 'text' || e.text.trim())
        .map(e => ({
          ...e,
          key: `${e.kind}-${e.id}`,
          kind: e.kind !== 'text' ? 'tool' : e.id === answerId ? 'answer' : 'note'
        }))
    },

    // What the failure report needs, flattened out of the message. `prompt` and
    // `context` are only on turns recorded after the report shipped, so an older
    // saved session degrades to "not captured" instead of breaking.
    diagnostics() {
      const m = this.message
      const context = m.context || {}

      return {
        prompt: m.prompt || '',
        plan: m.plan || '',
        answer: m.answer || '',
        tools: m.tools || [],
        endpoint: context.endpoint || '',
        conversationId: context.conversationId || ''
      }
    },

    // The final text block, which is where any table worth charting lives.
    answerText() {
      if (!this.timeline) {
        return this.message.answer
      }

      const answer = this.timeline.filter(e => e.kind === 'answer').pop()
      return answer ? answer.text : ''
    },

    // Offer a chart only once the answer is finalized, so a half-streamed
    // table never parses. Returns null when nothing chartable is found.
    chartData() {
      if (this.message.role !== 'assistant' || this.message.status === 'streaming') {
        return null
      }

      return extractChartData(this.answerText)
    },

    // Computed (not a method) so it only recomputes when message.at or
    // message.lastAt actually change. Previously this was a method that
    // fell back to Date.now() on every render — the displayed duration
    // would creep up as the panel re-rendered for unrelated reasons.
    duration() {
      const msg = this.message
      if (!msg.at) {
        return ''
      }

      // While streaming we have no end yet; show a placeholder rather
      // than a constantly-growing number. The store stamps lastAt the
      // moment status flips to 'done' or 'error'.
      if (!msg.lastAt) {
        return '…'
      }

      const ms = Math.max(0, msg.lastAt - msg.at)
      if (ms < 1000) {
        return `${ms}ms`
      }

      return `${(ms / 1000).toFixed(1)}s`
    }
  },

  methods: {
    isToolOpen(id) {
      return Boolean(this.expandedTools[id])
    },
    toggleTool(id) {
      this.$set(this.expandedTools, id, !this.expandedTools[id])
    },
    formatTime(ts) {
      if (!ts) {
        return ''
      }

      const d = new Date(ts)
      return d.toLocaleTimeString([], { hour12: false })
    },
    formatJson(value) {
      try {
        return JSON.stringify(value, null, 2)
      } catch (e) {
        return String(value)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$ai-mono-font: 'SF Mono', 'JetBrains Mono', 'Menlo', 'Consolas', monospace;
$ai-ui-font: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;

.ai-cli-msg {
  font-family: $ai-mono-font;
  font-size: 0.78rem;
  line-height: 1.55;
  color: #cdd6e3;

  & + & {
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px dashed rgba(143, 166, 200, 0.18);
  }

  &__user {
    display: flex;
    align-items: baseline;
    gap: 8px;
    color: #ffffff;
  }

  &__prompt {
    color: #6ee7b7;
    font-weight: 700;
    flex-shrink: 0;
  }

  &__user-text {
    flex: 1;
    word-break: break-word;
    white-space: pre-wrap;
  }

  &__time {
    color: #6b7fa8;
    font-size: 0.7rem;
    flex-shrink: 0;
  }

  &__assistant {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 6px;
    // Let children shrink instead of forcing the panel wider than the viewport
    // (the classic flexbox min-width:auto overflow that caused a second
    // horizontal scrollbar).
    min-width: 0;
  }

  &__block {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    min-width: 0;
  }

  &__block-text {
    flex: 1;
    min-width: 0;
    word-break: break-word;
    white-space: pre-wrap;
  }

  &__tag {
    color: #6b7fa8;
    flex-shrink: 0;
    user-select: none;
    font-weight: 600;

    &--plan   { color: #c4b5fd; }
    // Same width as "[ok  ]" so notes and tool rows share a left edge.
    &--note   { color: #4d5f80; min-width: 6ch; }
    &--running { color: #fbbf24; }
    &--ok     { color: #6ee7b7; }
    &--answer { color: #7ea9f5; }
    &--err    { color: #f87171; }
    &--done   { color: #6b7fa8; }
    &--link   { color: #c4b5fd; }
  }

  &__tool {
    flex-direction: column;
    gap: 4px;
    padding-left: 0;
    min-width: 0;
  }

  &__tool-head {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    appearance: none;
    background: transparent;
    border: 0;
    margin: 0;
    padding: 2px 4px;
    border-radius: 4px;
    font: inherit;
    color: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover { background: rgba(143, 166, 200, 0.08); }
  }

  &__tool-name {
    color: #ffffff;
    font-family: inherit;
    background: transparent;
    padding: 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__tool-meta {
    margin-left: auto;
    flex-shrink: 0;
    color: #6b7fa8;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  &__chev {
    flex-shrink: 0;
    color: #6b7fa8;
    display: inline-block;
    line-height: 1;
    transform: rotate(90deg);
    transition: transform 0.15s ease;

    &--open { transform: rotate(-90deg); }
  }

  &__json {
    // Full width of the message column, no big left indent, so tool payloads
    // use all the available area. The block wraps and flows into the single
    // panel scroll instead of owning its own scrollbar — these are collapsed
    // by default, so length only matters once an admin expands them.
    margin: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 8px 12px;
    background: rgba(143, 166, 200, 0.06);
    border-left: 2px solid rgba(143, 166, 200, 0.25);
    border-radius: 0 4px 4px 0;
    color: #adbacf;
    font-size: 0.72rem;
    line-height: 1.45;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: anywhere;

    &--args   { border-left-color: rgba(196, 181, 253, 0.5); }
    &--result { border-left-color: rgba(110, 231, 183, 0.5); }
  }

  // A progress note: what the model was about to look up. Dimmed, because the
  // answer below repeats anything that matters.
  &__note {
    color: #7b8cad;
    font-size: 0.74rem;
  }

  // The answer is the one block an admin actually reads, so it leaves the
  // terminal aesthetic behind: proportional type for prose, monospace kept for
  // ids, code and amount columns where character alignment carries meaning.
  &__answer {
    flex: 1;
    min-width: 0;
    padding: 10px 14px;
    background: rgba(126, 169, 245, 0.05);
    border-left: 2px solid rgba(126, 169, 245, 0.45);
    border-radius: 0 6px 6px 0;
    font-family: $ai-ui-font;
    font-size: 0.84rem;
    line-height: 1.6;
    color: #dce6f5;

    ::v-deep > :first-child { margin-top: 0; }
    ::v-deep > :last-child { margin-bottom: 0; }

    ::v-deep p {
      margin: 0 0 8px;
    }

    ::v-deep h3, ::v-deep h4, ::v-deep h5, ::v-deep h6 {
      margin: 16px 0 6px;
      color: #ffffff;
      line-height: 1.35;
    }
    ::v-deep h3 { font-size: 1rem; font-weight: 700; }
    ::v-deep h4 {
      font-size: 0.92rem;
      font-weight: 600;
      padding-bottom: 4px;
      border-bottom: 1px solid rgba(143, 166, 200, 0.2);
    }
    ::v-deep h5 { font-size: 0.85rem; font-weight: 600; color: #c7d6ec; }
    ::v-deep h6 { font-size: 0.82rem; font-weight: 600; color: #c7d6ec; }

    ::v-deep strong { color: #ffffff; font-weight: 650; }
    ::v-deep a { color: #7ea9f5; }

    ::v-deep code {
      background: rgba(143, 166, 200, 0.12);
      color: #6ee7b7;
      padding: 0 4px;
      border-radius: 3px;
      font-family: $ai-mono-font;
      font-size: 0.94em;
    }

    ::v-deep ul, ::v-deep ol {
      margin: 0 0 8px;
      padding-left: 20px;
    }
    ::v-deep li { margin-bottom: 3px; }
    ::v-deep li::marker { color: #6b7fa8; }
    ::v-deep li > ul, ::v-deep li > ol { margin: 3px 0 0; }

    ::v-deep hr {
      margin: 14px 0;
      border: 0;
      border-top: 1px solid rgba(143, 166, 200, 0.2);
    }

    ::v-deep blockquote {
      margin: 8px 0;
      padding: 4px 0 4px 10px;
      border-left: 2px solid rgba(196, 181, 253, 0.5);
      color: #b9c6dd;
    }

    // Horizontal scroll on the table alone, so a wide one never widens the pane.
    ::v-deep .ai-md-table-wrap {
      margin: 10px 0;
      overflow-x: auto;
      border: 1px solid rgba(143, 166, 200, 0.18);
      border-radius: 6px;
    }
    ::v-deep table {
      border-collapse: collapse;
      width: 100%;
      font-size: 0.78rem;
    }
    ::v-deep th, ::v-deep td {
      padding: 5px 10px;
      border-bottom: 1px solid rgba(143, 166, 200, 0.14);
      text-align: left;
      white-space: nowrap;
    }
    ::v-deep th {
      background: rgba(143, 166, 200, 0.09);
      color: #ffffff;
      font-weight: 600;
      font-size: 0.72rem;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      white-space: normal;
    }
    ::v-deep tbody tr:last-child td { border-bottom: 0; }
    ::v-deep tbody tr:hover td { background: rgba(143, 166, 200, 0.05); }

    // Tabular numerals keep amount columns aligned digit for digit.
    ::v-deep .ai-md-num {
      font-family: $ai-mono-font;
      font-variant-numeric: tabular-nums;
    }

    ::v-deep pre {
      margin: 8px 0;
      padding: 8px 10px;
      background: rgba(143, 166, 200, 0.08);
      border-radius: 4px;
      font-family: $ai-mono-font;
      font-size: 0.74rem;
      white-space: pre-wrap;
      word-break: break-word;
    }
    ::v-deep pre code { background: transparent; padding: 0; color: #cdd6e3; }
  }

  &__spinner {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 2px solid rgba(251, 191, 36, 0.3);
    border-top-color: #fbbf24;
    animation: ai-cli-spin 0.7s linear infinite;
  }

  &__pulse {
    color: #6b7fa8;
    animation: ai-cli-pulse 1.6s ease-in-out infinite;
  }

  &__links {
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
  }

  &__link {
    color: #7ea9f5;
    text-decoration: underline;
    text-underline-offset: 2px;

    &:hover { color: #c7d2f7; }
  }

  &__chart-wrap {
    flex: 1;
    min-width: 0;
  }

  &__chart-toggle {
    appearance: none;
    background: rgba(126, 169, 245, 0.12);
    border: 1px solid rgba(126, 169, 245, 0.3);
    color: #7ea9f5;
    font-family: inherit;
    font-size: 0.74rem;
    padding: 2px 10px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover { background: rgba(126, 169, 245, 0.2); }
  }
}

@keyframes ai-cli-spin { to { transform: rotate(360deg); } }
@keyframes ai-cli-pulse {
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
}
</style>
